import axios from 'axios'
import { toast } from 'sonner'
import { API_CONFIG } from '@/config/api'
import { useBoundStore } from '@/store/useBoundStore'
import type { APIResponse } from '@/types/api'

/**
 * Instancia de axios configurada para la API
 */
export const api = axios.create({
	baseURL: API_CONFIG.baseURL,
	headers: {
		'Content-Type': 'application/json',
	},
})

// Interceptor para añadir headers de autenticación en cada petición
api.interceptors.request.use(
	(config) => {
		// Excluir el endpoint de login ya que aún no hay usuario autenticado
		if (config.url?.includes('/auth/login')) {
			return config
		}

		const { user, company } = useBoundStore.getState()

		if (user) {
			config.headers.Authorization = user.code
			config.headers.company = user.company.code
			config.headers.adminCompany = user.isAdmin && company ? company.code : null
		}

		return config
	},
	(error) => {
		return Promise.reject(error)
	}
)

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
	(response) => {
		// Si la respuesta tiene la estructura APIResponse, validar success
		if (response.data && typeof response.data === 'object' && 'success' in response.data) {
			const apiResponse = response.data as APIResponse<unknown>
			if (!apiResponse.success) {
				// La API devolvió una respuesta con success: false
				const message = apiResponse.message || 'Error en la petición'
				toast.error(message)
				return Promise.reject(new Error(message))
			}
		}
		return response
	},
	(error) => {
		if (error.response) {
			// El servidor respondió con un código de error
			const apiResponse = error.response.data
			const message = apiResponse?.message || apiResponse?.error || error.message || 'Error en la petición'
			toast.error(message)
			return Promise.reject(new Error(message))
		} else if (error.request) {
			// La petición se hizo pero no hubo respuesta
			const message = 'No se pudo conectar con el servidor'
			toast.error(message)
			return Promise.reject(new Error(message))
		} else {
			// Algo pasó al configurar la petición
			toast.error(error.message || 'Error en la petición')
			return Promise.reject(error)
		}
	}
)
